import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import apiClient from '../../services/apiClient';
import LoadingSpinner from '../common/LoadingSpinner';

const ContextDocumentsManager = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [documentToReplace, setDocumentToReplace] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);

  const [uploadForm, setUploadForm] = useState({
    document_name: '',
    document_type: 'general',
    description: '',
    file: null,
  });

  const documentTypes = [
    { value: 'general', label: 'General Company Info' },
    { value: 'product', label: 'Product/Service Documentation' },
    { value: 'capability', label: 'Capabilities Statement' },
    { value: 'past_performance', label: 'Past Performance' },
    { value: 'technical_spec', label: 'Technical Specifications' },
    { value: 'certification', label: 'Certifications & Compliance' },
    { value: 'case_study', label: 'Case Studies' },
  ];

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/api/context-documents');
      if (response.success) {
        setDocuments(response.data.documents || []);
      }
    } catch (err) {
      setError('Failed to load documents: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm(prev => ({
        ...prev,
        file,
        document_name: prev.document_name || file.name.replace(/\.[^/.]+$/, '')
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setUploadForm(prev => ({
        ...prev,
        file,
        document_name: prev.document_name || file.name.replace(/\.[^/.]+$/, '')
      }));
      setShowUploadForm(true);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.document_name) {
      setError('Please provide a file and document name');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('document', uploadForm.file);
      formData.append('document_name', uploadForm.document_name);
      formData.append('document_type', uploadForm.document_type);
      if (uploadForm.description) {
        formData.append('description', uploadForm.description);
      }

      const response = await apiClient.post('/api/context-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.success) {
        setSuccess('Document uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
        setUploadForm({ document_name: '', document_type: 'general', description: '', file: null });
        setShowUploadForm(false);
        await loadDocuments();
      }
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/api/context-documents/${documentId}`);
      if (response.success) {
        setSuccess('Document deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
        await loadDocuments();
      }
    } catch (err) {
      setError('Delete failed: ' + err.message);
    }
  };

  const handleToggleActive = async (documentId, currentStatus) => {
    try {
      const response = await apiClient.put(`/api/context-documents/${documentId}`, {
        is_active: !currentStatus
      });
      if (response.success) {
        await loadDocuments();
      }
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
  };

  const handleReorder = async (documentId, direction) => {
    const doc = documents.find(d => d.document_id === documentId);
    if (!doc) return;

    const newPriority = direction === 'up' ? doc.priority_order - 1 : doc.priority_order + 1;
    if (newPriority < 1) return;

    try {
      // Swap priorities
      const otherDoc = documents.find(d => d.priority_order === newPriority);
      const updates = [
        { document_id: documentId, priority_order: newPriority },
      ];
      if (otherDoc) {
        updates.push({ document_id: otherDoc.document_id, priority_order: doc.priority_order });
      }

      const response = await apiClient.put('/api/context-documents/reorder', { documents: updates });
      if (response.success) {
        await loadDocuments();
      }
    } catch (err) {
      setError('Reorder failed: ' + err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownload = async (doc) => {
    try {
      setError('');
      const blob = await apiClient.downloadContextDocument(doc.document_id);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`Downloaded ${doc.original_file_name}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Download failed: ' + err.message);
    }
  };

  const handleReplaceClick = (doc) => {
    setDocumentToReplace(doc);
    setReplaceFile(null);
    setShowReplaceModal(true);
    setError('');
  };

  const handleReplaceFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReplaceFile(file);
    }
  };

  const handleReplaceConfirm = async () => {
    if (!replaceFile || !documentToReplace) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setReplacing(true);
      setError('');

      const response = await apiClient.replaceContextDocument(documentToReplace.document_id, replaceFile);

      if (response.success) {
        setSuccess(`Document replaced successfully! Processing complete.`);
        setTimeout(() => setSuccess(''), 3000);
        setShowReplaceModal(false);
        setDocumentToReplace(null);
        setReplaceFile(null);
        await loadDocuments();
      }
    } catch (err) {
      setError('Replace failed: ' + err.message);
    } finally {
      setReplacing(false);
    }
  };

  const handleReplaceCancel = () => {
    setShowReplaceModal(false);
    setDocumentToReplace(null);
    setReplaceFile(null);
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
        <span className="ml-3 text-gray-600">Loading context documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FontAwesomeIcon icon="file-alt" className="mr-2 text-blue-500" />
            Context Documents
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Upload documents containing detailed company information, product specs, and past performance examples.
            Documents are sent to AI in priority order.
          </p>
        </div>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
        >
          <FontAwesomeIcon icon={showUploadForm ? 'times' : 'plus'} className="mr-2" />
          {showUploadForm ? 'Cancel' : 'Upload Document'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-100 border border-red-200 rounded-lg text-red-800">
          <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-100 border border-green-200 rounded-lg text-green-800">
          <FontAwesomeIcon icon="check-circle" className="mr-2" />
          {success}
        </div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <div className="bg-white rounded-lg shadow p-6 border-2 border-blue-200">
          <h4 className="font-semibold text-gray-900 mb-4">Upload New Document</h4>

          {/* File Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors ${
              dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
          >
            {uploadForm.file ? (
              <div className="flex items-center justify-center">
                <FontAwesomeIcon icon="file-pdf" className="text-4xl text-red-500 mr-3" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">{uploadForm.file.name}</p>
                  <p className="text-sm text-gray-600">{formatFileSize(uploadForm.file.size)}</p>
                </div>
                <button
                  onClick={() => setUploadForm(prev => ({ ...prev, file: null }))}
                  className="ml-4 text-red-600 hover:text-red-800"
                >
                  <FontAwesomeIcon icon="times-circle" />
                </button>
              </div>
            ) : (
              <div>
                <FontAwesomeIcon icon="cloud-upload-alt" className="text-4xl text-gray-400 mb-3" />
                <p className="text-gray-600 mb-2">Drag and drop a file here, or click to browse</p>
                <p className="text-sm text-gray-500">PDF, DOC, DOCX, or TXT (max 50MB)</p>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.txt"
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="mt-4 inline-block px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer"
                >
                  Choose File
                </label>
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Name *
              </label>
              <input
                type="text"
                value={uploadForm.document_name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, document_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Company Capabilities Overview 2024"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type *
              </label>
              <select
                value={uploadForm.document_type}
                onChange={(e) => setUploadForm(prev => ({ ...prev, document_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {documentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of what this document contains..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUploadForm(false);
                  setUploadForm({ document_name: '', document_type: 'general', description: '', file: null });
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadForm.file || !uploadForm.document_name}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 flex items-center"
              >
                {uploading ? (
                  <>
                    <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="upload" className="mr-2" />
                    Upload Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FontAwesomeIcon icon="file-alt" className="text-4xl text-gray-300 mb-4" />
          <p className="text-gray-600">No context documents uploaded yet.</p>
          <p className="text-sm text-gray-500 mt-2">
            Upload documents to provide AI with detailed company information for better proposals.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc, index) => (
            <div
              key={doc.document_id}
              className={`bg-white rounded-lg shadow border p-4 ${
                !doc.is_active ? 'opacity-50' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  {/* Priority Badge */}
                  <div className="flex flex-col items-center space-y-1">
                    <span className="text-xs font-medium text-gray-500">Priority</span>
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 font-bold">
                      {doc.priority_order}
                    </span>
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => handleReorder(doc.document_id, 'up')}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <FontAwesomeIcon icon="chevron-up" />
                      </button>
                      <button
                        onClick={() => handleReorder(doc.document_id, 'down')}
                        disabled={index === documents.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <FontAwesomeIcon icon="chevron-down" />
                      </button>
                    </div>
                  </div>

                  {/* Document Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{doc.document_name}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {documentTypes.find(t => t.value === doc.document_type)?.label || doc.document_type}
                        </p>
                        {doc.description && (
                          <p className="text-sm text-gray-500 mt-2">{doc.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                      <span>
                        <FontAwesomeIcon icon="file" className="mr-1" />
                        {doc.original_file_name}
                      </span>
                      <span>
                        <FontAwesomeIcon icon="save" className="mr-1" />
                        {formatFileSize(doc.file_size)}
                      </span>
                      <span>
                        <FontAwesomeIcon icon="file-alt" className="mr-1" />
                        {doc.text_length ? `${doc.text_length.toLocaleString()} chars` : 'Processing...'}
                      </span>
                      <span>
                        <FontAwesomeIcon icon="user" className="mr-1" />
                        {doc.uploaded_by_name}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        doc.processing_status === 'Completed' ? 'bg-green-100 text-green-800' :
                        doc.processing_status === 'Processing' ? 'bg-blue-100 text-blue-800' :
                        doc.processing_status === 'Failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {doc.processing_status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                    title="Download"
                  >
                    <FontAwesomeIcon icon="download" />
                  </button>
                  <button
                    onClick={() => handleReplaceClick(doc)}
                    className="p-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                    title="Replace"
                  >
                    <FontAwesomeIcon icon="sync-alt" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(doc.document_id, doc.is_active)}
                    className={`p-2 rounded-lg ${
                      doc.is_active
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                    title={doc.is_active ? 'Active' : 'Inactive'}
                  >
                    <FontAwesomeIcon icon={doc.is_active ? 'eye' : 'eye-slash'} />
                  </button>
                  <button
                    onClick={() => handleDelete(doc.document_id)}
                    className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon="trash" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <FontAwesomeIcon icon="info-circle" className="text-blue-500 mt-1 mr-3" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-2">How Priority Works:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Documents are sent to AI in priority order (1 is highest priority)</li>
              <li>Lower priority documents are included if there's token space available</li>
              <li>Inactive documents are never sent to AI</li>
              <li>Prioritize your most important company information and product details</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Replace Document Modal */}
      {showReplaceModal && documentToReplace && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon="sync-alt" className="text-purple-600 text-xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Replace Document
                </h3>
              </div>

              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  Replace <span className="font-medium">{documentToReplace.document_name}</span> with a new file:
                </p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Current file: <span className="font-mono text-xs">{documentToReplace.original_file_name}</span></p>
                  <p>Current size: {formatFileSize(documentToReplace.file_size)}</p>
                </div>
              </div>

              {/* File Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select New File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {replaceFile ? (
                    <div className="flex items-center justify-center">
                      <FontAwesomeIcon icon="file-pdf" className="text-3xl text-red-500 mr-3" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{replaceFile.name}</p>
                        <p className="text-sm text-gray-600">{formatFileSize(replaceFile.size)}</p>
                      </div>
                      <button
                        onClick={() => setReplaceFile(null)}
                        className="ml-4 text-red-600 hover:text-red-800"
                      >
                        <FontAwesomeIcon icon="times-circle" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FontAwesomeIcon icon="cloud-upload-alt" className="text-3xl text-gray-400 mb-2" />
                      <p className="text-gray-600 mb-2">Choose a file to upload</p>
                      <p className="text-xs text-gray-500 mb-3">PDF, DOC, DOCX, or TXT (max 50MB)</p>
                      <input
                        type="file"
                        onChange={handleReplaceFileSelect}
                        accept=".pdf,.doc,.docx,.txt"
                        className="hidden"
                        id="replace-file-upload"
                      />
                      <label
                        htmlFor="replace-file-upload"
                        className="inline-block px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer"
                      >
                        Choose File
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <div className="flex items-start">
                  <FontAwesomeIcon icon="exclamation-triangle" className="text-amber-600 mt-0.5 mr-2" />
                  <p className="text-sm text-amber-800">
                    The old file will be permanently deleted and replaced with the new file. Text will be re-extracted and the document will be re-processed.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleReplaceCancel}
                  disabled={replacing}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReplaceConfirm}
                  disabled={replacing || !replaceFile}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 flex items-center"
                >
                  {replacing ? (
                    <>
                      <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
                      Replacing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="sync-alt" className="mr-2" />
                      Replace Document
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextDocumentsManager;
